/**
 * TanStack Query bindings (§8).
 *
 * Two rules hold throughout:
 *
 *  - **The server owns the forecast.** Nothing here recomputes a projection.
 *    The timeline and dashboard are fetched, not derived, because the API runs
 *    the same engine the reminder job does and a second client-side answer
 *    could only ever disagree (§4, §8).
 *  - **Optimistic updates only where the answer is obvious.** "Mark checked"
 *    has one possible outcome, so we paint it immediately. A service log moves
 *    the whole forecast — where the next crossing lands, what the tally says —
 *    and guessing at that would mean redoing the engine's job in the UI. So we
 *    paint the cheap, local part and let the server tell us the rest.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';

import type {
  CreateCarRequest,
  CreateGarageRequest,
  CreateDriverRequest,
  CreateEventRequest,
  CreateItemRequest,
  CreateItemsRequest,
  CreateServiceLogRequest,
  CompleteEventRequest,
  DeleteUserRequest,
  UpdateMeRequest,
  InviteMemberRequest,
  ItemDto,
  ListUsersQuery,
  UpdateCarRequest,
  UpdateDriverRequest,
  UpdateEventRequest,
  UpdateGarageRequest,
  UpdateItemRequest,
} from '@svcdue/contracts';

import * as api from './endpoints';
import { ApiError } from './client';
import { keys } from './keys';
import { useAuth } from '../auth/AuthProvider';

/**
 * Retrying a 403 or a 422 just fails three more times, slowly, and delays the
 * error the user needs to see. Only genuinely transient failures are worth a
 * second attempt.
 */
function retryPolicy(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && !error.isRetryable) return false;
  return failureCount < 2;
}

export const queryDefaults = {
  retry: retryPolicy,
  /**
   * A garage's data changes when *this* user changes it, not on its own — a
   * crew member logging a service elsewhere is rare and not urgent. Half a
   * minute keeps tab-switching instant without serving genuinely stale forecasts.
   */
  staleTime: 30_000,
} satisfies Partial<UseQueryOptions>;

// --- identity -------------------------------------------------------------

/**
 * `GET /me` — the user, their memberships, and `isAdmin`.
 *
 * This is the app's authority on roles. The ID token in the browser also
 * carries claims, but nothing here reads them for authorization: the token is
 * self-asserted until the API verifies it, and only the API knows about
 * memberships at all.
 */
export function useMe(enabled: boolean) {
  return useQuery({ queryKey: keys.me(), queryFn: api.getMe, enabled, ...queryDefaults });
}

export function useGarages(enabled: boolean) {
  return useQuery({ queryKey: keys.garages(), queryFn: api.listGarages, enabled, ...queryDefaults });
}

/**
 * Delete the signed-in account and everything it owns (§11).
 *
 * On success the user no longer exists, so the only sane next state is signed
 * out — and `signOut` also `clear()`s the query cache, which is exactly right:
 * every cached garage, car and forecast just referenced a user that is gone.
 * Routed through the same sign-out path a normal logout uses so there is one
 * way out of a session, not two.
 */
/**
 * Flip a reminder preference (§6).
 *
 * No optimistic update: the toggle is not urgent, and showing "off" before the
 * server agrees would be a lie about whether mail will be sent. It invalidates
 * `me` so the checkbox re-renders from what was actually stored.
 */
export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMeRequest) => api.updateMe(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.me() }),
  });
}

export function useDeleteAccount() {
  const { signOut } = useAuth();
  return useMutation({
    mutationFn: (body: DeleteUserRequest) => api.deleteMe(body),
    onSuccess: () => signOut(),
  });
}

// --- views ----------------------------------------------------------------

export function useDashboard(garageId: string | undefined) {
  return useQuery({
    queryKey: keys.dashboard(garageId ?? ''),
    queryFn: () => api.getDashboard(garageId!),
    enabled: Boolean(garageId),
    ...queryDefaults,
  });
}

export function useTimeline(garageId: string | undefined, carId?: string) {
  return useQuery({
    queryKey: keys.timeline(garageId ?? '', carId),
    queryFn: () => api.getTimeline(garageId!, carId),
    enabled: Boolean(garageId),
    ...queryDefaults,
  });
}

/** The all-time record behind the timeline's History tab. Filtered client-side. */
export function useHistory(garageId: string | undefined) {
  return useQuery({
    queryKey: keys.history(garageId ?? ''),
    queryFn: () => api.getHistory(garageId!),
    enabled: Boolean(garageId),
    ...queryDefaults,
  });
}

export function useCars(garageId: string | undefined) {
  return useQuery({
    queryKey: keys.cars(garageId ?? ''),
    queryFn: () => api.listCars(garageId!),
    enabled: Boolean(garageId),
    ...queryDefaults,
  });
}

export function useDrivers(garageId: string | undefined) {
  return useQuery({
    queryKey: keys.drivers(garageId ?? ''),
    queryFn: () => api.listDrivers(garageId!),
    enabled: Boolean(garageId),
    ...queryDefaults,
  });
}

export function useItems(garageId: string | undefined) {
  return useQuery({
    queryKey: keys.items(garageId ?? ''),
    queryFn: () => api.listItems(garageId!),
    enabled: Boolean(garageId),
    ...queryDefaults,
  });
}

export function useEvents(garageId: string | undefined) {
  return useQuery({
    queryKey: keys.events(garageId ?? ''),
    queryFn: () => api.listEvents(garageId!),
    enabled: Boolean(garageId),
    ...queryDefaults,
  });
}

/** Owner-only. Crew calling this would 404, so the screen is gated before it mounts. */
export function useMembers(garageId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.members(garageId ?? ''),
    queryFn: () => api.listMembers(garageId!),
    enabled: Boolean(garageId) && enabled,
    ...queryDefaults,
  });
}

export function usePublicGarage(token: string) {
  return useQuery({
    queryKey: keys.publicGarage(token),
    queryFn: () => api.getPublicGarage(token),
    ...queryDefaults,
  });
}

// --- garage mutations -----------------------------------------------------

/**
 * Creating a garage changes what `/me` and `/garages` say, since it also
 * creates the owner membership every later authorization check reads.
 */
export function useCreateGarage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateGarageRequest) => api.createGarage(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.garages() });
      void qc.invalidateQueries({ queryKey: keys.me() });
    },
  });
}

/**
 * Not optimistic, deliberately. Turning sharing on mints a token only the
 * server can produce, and the whole point of the toggle is to reveal that
 * link — an optimistic `isPublic: true` with no URL under it would render a
 * share panel with nothing to share.
 */
export function useSetPublic(garageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (isPublic: boolean) => api.setGaragePublic(garageId, isPublic),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.garages() });
      void qc.invalidateQueries({ queryKey: keys.me() });
    },
  });
}

/**
 * Garage settings that are NOT the public on/off toggle — today, whether the
 * public link exposes maintenance detail. Kept off `useSetPublic` on purpose:
 * this must not re-mint the share token, so an owner can flip part-number
 * visibility without breaking the link they already handed out.
 */
export function useUpdateGarage(garageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateGarageRequest) => api.updateGarage(garageId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.garages() }),
  });
}

export function useInviteMember(garageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InviteMemberRequest) => api.inviteMember(garageId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.members(garageId) }),
  });
}

export function useRemoveMember(garageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.removeMember(garageId, userId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.members(garageId) }),
  });
}

// --- fleet mutations (owner only) ----------------------------------------

export function useCreateCar(garageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCarRequest) => api.createCar(garageId, body),
    // A new car changes the dashboard's car list and the tallies with it.
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.garage(garageId) }),
  });
}

export function useUpdateCar(garageId: string, carId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateCarRequest) => api.updateCar(garageId, carId, body),
    // Editing a car can move the forecast — switching a car from strip to street
    // turns mileage triggers on, and the odometer feeds them — so invalidate the
    // whole garage, not just the car list.
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.garage(garageId) }),
  });
}

export function useDeleteCar(garageId: string) {
  const qc = useQueryClient();
  return useMutation({
    // The API cascades: deleting a car takes its parts and events with it
    // (routes/fleet.ts). So this can empty a good part of the dashboard, and the
    // whole garage is refetched.
    mutationFn: (carId: string) => api.deleteCar(garageId, carId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.garage(garageId) }),
  });
}

export function useCreateDriver(garageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDriverRequest) => api.createDriver(garageId, body),
    // A new driver is a new owner for safety gear and a new pick in the event
    // form; the garage's whole read-set can reference it, so invalidate wholesale.
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.garage(garageId) }),
  });
}

export function useUpdateDriver(garageId: string, driverId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateDriverRequest) => api.updateDriver(garageId, driverId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.garage(garageId) }),
  });
}

export function useDeleteDriver(garageId: string) {
  const qc = useQueryClient();
  return useMutation({
    // The API cascades: deleting a driver takes their safety gear — the items
    // with `ownerType: 'driver'` — with them (routes/fleet.ts). That gear was on
    // the driver screen and in the timeline's certs, so the garage is refetched.
    mutationFn: (driverId: string) => api.deleteDriver(garageId, driverId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.garage(garageId) }),
  });
}

export function useCreateItem(garageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateItemRequest) => api.createItem(garageId, body),
    // A new part appears in the forecast immediately — it may already be due.
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.garage(garageId) }),
  });
}

/**
 * Create a whole kit in one request (the safety-equipment starter).
 *
 * One mutation, not a client-side loop: the API commits every item in a single
 * transaction, so there is no partial-success state to paint and — unlike firing
 * N concurrent creates — nothing for DSQL's optimistic concurrency to abort. A
 * new set of parts can already be due, so the whole garage is invalidated once.
 */
export function useCreateItems(garageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateItemsRequest) => api.createItems(garageId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.garage(garageId) }),
  });
}

export function useUpdateItem(garageId: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    // Editing a part changes the forecast: a new interval, a new last-serviced
    // date or a flipped safety flag all move where — and whether — it comes due.
    mutationFn: (body: UpdateItemRequest) => api.updateItem(garageId, itemId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.garage(garageId) }),
  });
}

export function useDeleteItem(garageId: string) {
  const qc = useQueryClient();
  return useMutation({
    // Removing a part removes it from the forecast — and takes its service
    // history with it (routes/items.ts) — so the garage is refetched.
    mutationFn: (itemId: string) => api.deleteItem(garageId, itemId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.garage(garageId) }),
  });
}

// --- events: owner or crew ------------------------------------------------

/**
 * Book a planned event.
 *
 * Owner or crew — booking the weekend is a crew action (routes/events.ts). A new
 * event is a new deadline the whole forecast reorganises around: it changes what
 * is "due before your next event", where mid-event crossings land, and the
 * dashboard's next-event tile. So it invalidates the garage wholesale rather
 * than just the event list.
 */
export function useCreateEvent(garageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateEventRequest) => api.createEvent(garageId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.garage(garageId) }),
  });
}

export function useUpdateEvent(garageId: string, eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    // Moving an event's date or estimates redraws the forecast around it, so the
    // whole garage refetches, same as creating one.
    mutationFn: (body: UpdateEventRequest) => api.updateEvent(garageId, eventId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.garage(garageId) }),
  });
}

export function useDeleteEvent(garageId: string) {
  const qc = useQueryClient();
  return useMutation({
    // Removing an event removes a deadline; anything the forecast had placed
    // around it moves, so refetch the garage.
    mutationFn: (eventId: string) => api.deleteEvent(garageId, eventId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.garage(garageId) }),
  });
}

/**
 * Record an event's actuals — the single most consequential write in the app.
 *
 * Converting a planned event to `actual` replaces its estimate with a fact and
 * advances real usage, which moves every counter on the car: where the next
 * crossing lands, what is due before the next event, the dashboard tallies. None
 * of that is knowable in the browser, so — like logging a service — this paints
 * nothing optimistically and invalidates the whole garage.
 */
export function useCompleteEvent(garageId: string, eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CompleteEventRequest) => api.completeEvent(garageId, eventId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.garage(garageId) }),
  });
}

// --- the quick actions: owner or crew ------------------------------------

/**
 * Log a service (§8, "optimistic updates for quick actions").
 *
 * Optimism stops at the item's own `lastServicedAt`, which is the one thing we
 * can predict exactly — the user just told us the date. What we deliberately do
 * *not* guess is the forecast: resetting an item's counters moves its next
 * crossing, possibly past an event, possibly changing the car's tally and the
 * "before you load the trailer" list. Reproducing that in the browser means
 * running the projection engine client-side against usage we do not have, which
 * is exactly the duplication §8 says to avoid. So the item list paints
 * instantly, the forecast refetches, and the two agree because only one of them
 * ever did the maths.
 */
export function useLogService(garageId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: CreateServiceLogRequest }) =>
      api.createServiceLog(garageId, itemId, body),

    onMutate: async ({ itemId, body }) => {
      // In-flight refetches would land after our optimistic write and undo it.
      await qc.cancelQueries({ queryKey: keys.items(garageId) });
      const previous = qc.getQueryData<ItemDto[]>(keys.items(garageId));

      qc.setQueryData<ItemDto[]>(keys.items(garageId), (items) =>
        items?.map((item) => (item.id === itemId ? { ...item, lastServicedAt: body.servicedAt } : item)),
      );

      return { previous };
    },

    onError: (_err, _vars, context) => {
      // Put back exactly what was there. Not "refetch" — the server may be the
      // thing that failed, and a rollback must not depend on it answering.
      if (context?.previous) qc.setQueryData(keys.items(garageId), context.previous);
    },

    onSettled: () => void qc.invalidateQueries({ queryKey: keys.garage(garageId) }),
  });
}

/** Undo a service log. Same reasoning as logging one, in reverse. */
export function useUndoService(garageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, logId }: { itemId: string; logId: string }) =>
      api.deleteServiceLog(garageId, itemId, logId),
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.garage(garageId) }),
  });
}

/**
 * "Mark checked today" on an inspect card.
 *
 * The safest optimistic update in the app: an inspect window is a pure function
 * of `lastCheckedAt` and the interval, both of which we already hold, so the
 * card's new state is fully known before the server answers. The status pill
 * flips green the moment it is tapped.
 */
export function useMarkChecked(garageId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, checkedAt }: { itemId: string; checkedAt: string }) =>
      api.markChecked(garageId, itemId, { checkedAt }),

    onMutate: async ({ itemId, checkedAt }) => {
      await qc.cancelQueries({ queryKey: keys.items(garageId) });
      const previous = qc.getQueryData<ItemDto[]>(keys.items(garageId));

      qc.setQueryData<ItemDto[]>(keys.items(garageId), (items) =>
        items?.map((item) =>
          item.id === itemId
            ? {
                ...item,
                // Only the inspect trigger moves; an item can carry others, and
                // marking it checked says nothing about its mileage interval.
                triggers: item.triggers.map((t) => (t.type === 'inspect' ? { ...t, lastCheckedAt: checkedAt } : t)),
              }
            : item,
        ),
      );

      return { previous };
    },

    onError: (_err, _vars, context) => {
      if (context?.previous) qc.setQueryData(keys.items(garageId), context.previous);
    },

    onSettled: () => void qc.invalidateQueries({ queryKey: keys.garage(garageId) }),
  });
}

// --- admin (§9) -----------------------------------------------------------

export function useAdminUsers(query: ListUsersQuery, enabled: boolean) {
  return useQuery({
    queryKey: keys.admin.users(query),
    queryFn: () => api.listUsers(query),
    enabled,
    ...queryDefaults,
  });
}

export function useAdminUser(userId: string | undefined) {
  return useQuery({
    queryKey: keys.admin.user(userId ?? ''),
    queryFn: () => api.getUserDetail(userId!),
    enabled: Boolean(userId),
    ...queryDefaults,
  });
}

export function useAdminStats(days: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.admin.stats(days),
    queryFn: () => api.getStats(days),
    enabled,
    ...queryDefaults,
  });
}

/**
 * Costs are cached server-side for 6 hours because Cost Explorer bills $0.01
 * per request (§9). Refetching on window focus would quietly turn an admin
 * tab-switching habit into a line item, so this query is pinned: no refetch on
 * focus, no refetch on mount, and a stale time matching the server's TTL.
 */
export function useAdminCosts(days: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.admin.costs(days),
    queryFn: () => api.getCosts(days),
    enabled,
    retry: false,
    staleTime: 6 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

export function useSetUserDisabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, disabled }: { userId: string; disabled: boolean }) =>
      api.setUserDisabled(userId, disabled),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin'] }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, body }: { userId: string; body: DeleteUserRequest }) => api.deleteUser(userId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin'] }),
  });
}
